import { z } from 'zod';
import { emailSchema } from './schemas/auth.js';

/**
 * Help & Support tickets: a signed-in customer raises one from their account, it opens as a chat thread, and staff answer
 * it from the admin inbox. The topics, statuses and attachment limits live here so the storefront, the admin and the API
 * agree on them. (The phone numbers and hours at the top of the storefront are a different thing: see schemas/support.ts.)
 */

/** What a ticket is about. Add a topic here and it shows up in the form, the admin filters and the emails. */
export const SUPPORT_CATEGORIES = [
  { key: 'ORDER_ISSUE', label: 'Order issue', hint: 'Something is wrong with an order you placed' },
  { key: 'PAYMENT', label: 'Payment', hint: 'A charge, a failed payment or cash on delivery' },
  { key: 'SHIPPING', label: 'Shipping & delivery', hint: 'Where your parcel is, or a delivery problem' },
  { key: 'RETURNS_REFUND', label: 'Returns & refunds', hint: 'Sending something back, or a refund you are waiting for' },
  { key: 'PRODUCT_QUESTION', label: 'Product question', hint: 'Sizes, materials, how to use a kit' },
  { key: 'CUSTOM_ORDER', label: 'Custom order', hint: 'A commission or a custom request' },
  { key: 'ACCOUNT', label: 'My account', hint: 'Signing in, your details, your email' },
  { key: 'OTHER', label: 'Something else', hint: 'Anything that does not fit above' },
] as const;
export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number]['key'];
const CATEGORY_KEYS = SUPPORT_CATEGORIES.map((c) => c.key) as [SupportCategory, ...SupportCategory[]];
export const supportCategoryLabel = (key: string): string => SUPPORT_CATEGORIES.find((c) => c.key === key)?.label ?? key;

/**
 * OPEN: waiting on us. IN_PROGRESS: someone is on it. AWAITING_CUSTOMER: we answered and wait for the customer.
 * CLOSED: done; the customer can read it but not reply (staff can reopen it, or they raise a new one).
 */
export const SupportTicketStatus = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  AWAITING_CUSTOMER: 'AWAITING_CUSTOMER',
  CLOSED: 'CLOSED',
} as const;
export type SupportTicketStatus = (typeof SupportTicketStatus)[keyof typeof SupportTicketStatus];
const STATUS_KEYS = Object.values(SupportTicketStatus) as [SupportTicketStatus, ...SupportTicketStatus[]];

/** How each status reads to the customer and to staff. */
export const SUPPORT_STATUS_LABELS: Record<SupportTicketStatus, { customer: string; staff: string }> = {
  OPEN: { customer: 'Open', staff: 'Open' },
  IN_PROGRESS: { customer: 'Being looked at', staff: 'In progress' },
  AWAITING_CUSTOMER: { customer: 'Replied', staff: 'Awaiting customer' },
  CLOSED: { customer: 'Closed', staff: 'Closed' },
};

// ------------------------------------------------------------ attachments --

export const SUPPORT_ATTACHMENT_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'] as const;
export const SUPPORT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const SUPPORT_MAX_ATTACHMENTS = 5;
export const SUPPORT_MESSAGE_MAX = 5000;
export const SUPPORT_SUBJECT_MAX = 150;

/** Step one of attaching a file: ask for somewhere to upload it. */
export const supportUploadSchema = z.object({
  filename: z.string().trim().min(1).max(200),
  contentType: z.enum(SUPPORT_ATTACHMENT_TYPES, { errorMap: () => ({ message: 'Attach a photo (JPG, PNG, WebP) or a PDF' }) }),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(SUPPORT_ATTACHMENT_MAX_BYTES, `Each file can be at most ${SUPPORT_ATTACHMENT_MAX_BYTES / 1_048_576} MB`),
});
export type SupportUploadInput = z.infer<typeof supportUploadSchema>;

const attachmentIds = z.array(z.string().uuid()).max(SUPPORT_MAX_ATTACHMENTS, `Attach at most ${SUPPORT_MAX_ATTACHMENTS} files`).default([]);
const messageBody = z.string().trim().min(1, 'Write a message').max(SUPPORT_MESSAGE_MAX, `Keep it under ${SUPPORT_MESSAGE_MAX} characters`);

// ---------------------------------------------------------------- customer --

export const createSupportTicketSchema = z.object({
  category: z.enum(CATEGORY_KEYS, { errorMap: () => ({ message: 'Pick a topic' }) }),
  subject: z.string().trim().min(3, 'Add a short subject').max(SUPPORT_SUBJECT_MAX),
  body: messageBody,
  /** one of the customer's own orders, by id or number */
  orderId: z.string().trim().min(1).max(64).optional(),
  /** required when the account has no email yet; it is saved to the account */
  email: emailSchema.optional(),
  attachmentIds,
});
export type CreateSupportTicketInput = z.input<typeof createSupportTicketSchema>;

export const supportReplySchema = z.object({ body: messageBody, attachmentIds });
export type SupportReplyInput = z.input<typeof supportReplySchema>;

// ------------------------------------------------------------------- staff --

export const staffSupportReplySchema = supportReplySchema.extend({
  /** a note only staff can see */
  internal: z.boolean().default(false),
  /** where the ticket goes after this reply; by default a public reply waits on the customer and a note changes nothing */
  status: z.enum(STATUS_KEYS).optional(),
});
export type StaffSupportReplyInput = z.input<typeof staffSupportReplySchema>;

export const supportTicketUpdateSchema = z
  .object({
    status: z.enum(STATUS_KEYS).optional(),
    assigneeId: z.string().uuid().nullable().optional(),
    departmentId: z.string().uuid().nullable().optional(),
    category: z.enum(CATEGORY_KEYS).optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'Nothing to change');
export type SupportTicketUpdateInput = z.input<typeof supportTicketUpdateSchema>;

export const supportTicketListQuerySchema = z.object({
  status: z.union([z.enum(STATUS_KEYS), z.literal('ACTIVE')]).optional(),
  category: z.enum(CATEGORY_KEYS).optional(),
  /** a staff id, "me" or "none" */
  assignee: z.string().max(64).optional(),
  /** a department id or "none" */
  department: z.string().max(64).optional(),
  q: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type SupportTicketListQuery = z.input<typeof supportTicketListQuerySchema>;

export const supportDepartmentSchema = z.object({
  name: z.string().trim().min(2, 'Name it').max(60),
  description: z.string().trim().max(200).default(''),
  /** a shared inbox told when a ticket is routed here; optional */
  email: z.union([z.literal(''), emailSchema]).default(''),
  isActive: z.boolean().default(true),
});
export type SupportDepartmentInput = z.input<typeof supportDepartmentSchema>;

// -------------------------------------------------------------------- DTOs --

export interface SupportAttachmentDTO {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  /** opens the file (a short-lived signed link, only for the customer and staff) */
  url: string;
}

export interface SupportMessageDTO {
  id: string;
  author: 'CUSTOMER' | 'STAFF' | 'SYSTEM';
  /** the first name customers see for staff ("Sana from MeshSmith"), the customer's name for staff */
  authorName: string;
  body: string;
  internal: boolean;
  attachments: SupportAttachmentDTO[];
  createdAt: string;
}

export interface SupportTicketSummaryDTO {
  id: string;
  number: string;
  subject: string;
  category: SupportCategory;
  status: SupportTicketStatus;
  orderNumber: string | null;
  lastMessageAt: string;
  createdAt: string;
  /** a reply the viewer has not opened yet */
  unread: boolean;
}

export interface SupportTicketDTO extends SupportTicketSummaryDTO {
  email: string;
  orderId: string | null;
  closedAt: string | null;
  messages: SupportMessageDTO[];
}

export interface StaffRefDTO {
  id: string;
  name: string;
  email: string | null;
}

export interface SupportDepartmentDTO {
  id: string;
  name: string;
  description: string;
  email: string;
  isActive: boolean;
  openTickets: number;
}

export interface AdminSupportTicketSummaryDTO extends SupportTicketSummaryDTO {
  customer: { id: string; name: string; email: string; phone: string | null };
  assignee: StaffRefDTO | null;
  department: { id: string; name: string } | null;
  messageCount: number;
}

export interface AdminSupportTicketDTO extends AdminSupportTicketSummaryDTO {
  email: string;
  orderId: string | null;
  closedAt: string | null;
  messages: SupportMessageDTO[];
}

export interface SupportInboxCountsDTO {
  open: number;
  inProgress: number;
  awaitingCustomer: number;
  mine: number;
  unassigned: number;
}
